import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import {
  Wallet, CreditCard, ArrowUpRight, FileText,
  TrendingUp, Plus, ExternalLink
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell, TableWrapper } from '@/components/ui/table'
import { getSupabaseWithClerkAuth } from '@/lib/supabase'
import { formatCurrency, CURRENCIES, CURRENCY_NAMES, CURRENCY_SYMBOLS } from '@/lib/currency'
import { useToast } from '@/components/ui/toast'
import type { Transaction } from '@/lib/supabase'

const TOP_UP_AMOUNTS_USD = [5, 10, 25, 50, 100, 250]


function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export default function BillingPage() {
  const { getToken } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [topUpOpen, setTopUpOpen] = useState(false)
  const [topUpAmount, setTopUpAmount] = useState(10)
  const [customAmount, setCustomAmount] = useState('')

  const { data: customer } = useQuery({
    queryKey: ['customer'],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db.from('customers').select('*').single()
      return data || { credit_balance_usd: 10.00, currency_preference: 'PHP' }
    },
  })

  const currency = customer?.currency_preference || 'PHP'
  const balance = customer?.credit_balance_usd ?? 10.00

  const { data: transactions = [] } = useQuery<Transaction[]>({
    queryKey: ['transactions'],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db
        .from('transactions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50)
      return data || []
    },
  })

  const currencyMutation = useMutation({
    mutationFn: async (newCurrency: string) => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { error } = await db
        .from('customers')
        .update({ currency_preference: newCurrency })
        .eq('id', customer?.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer'] })
      toast({ title: 'Currency preference updated', variant: 'success' })
    },
  })

  const finalAmount = customAmount ? parseFloat(customAmount) : topUpAmount

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Balance + top-up */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Current Balance</p>
                <p className="mt-2 text-4xl font-bold text-gray-900">
                  {formatCurrency(balance, currency)}
                </p>
                <p className="mt-1 text-sm text-gray-500">≈ ${balance.toFixed(2)} USD</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-xl">
                <Wallet className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              <Button onClick={() => setTopUpOpen(true)}>
                <Plus className="h-4 w-4" />
                Top Up Balance
              </Button>
              <Button variant="outline">
                <FileText className="h-4 w-4" />
                Download Invoice
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-500 mb-3">Currency Preference</p>
            <Select
              value={currency}
              onValueChange={(v) => currencyMutation.mutate(v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c} value={c}>
                    {CURRENCY_SYMBOLS[c]} {c} — {CURRENCY_NAMES[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-gray-400">
              Affects how prices are displayed. Billing is in USD.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Plan info */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-50 rounded-xl">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">Pay-As-You-Go</h3>
                  <Badge variant="success">Active</Badge>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  No monthly fees. Rate limit: 1,000 RPM · 10M tokens/day
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm">
              <ExternalLink className="h-3.5 w-3.5" />
              Upgrade Plan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transaction history */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-3">Transaction History</h3>
        <TableWrapper>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount (USD)</TableHead>
                <TableHead>Amount ({currency})</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-gray-400">
                    No transactions yet — top up your balance to get started.
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map(txn => (
                  <TableRow key={txn.id}>
                    <TableCell>{txn.description}</TableCell>
                    <TableCell>
                      <Badge variant={txn.type === 'topup' ? 'success' : txn.type === 'refund' ? 'default' : 'secondary'}>
                        {txn.type}
                      </Badge>
                    </TableCell>
                    <TableCell className={txn.amount_usd >= 0 ? 'text-green-600 font-medium' : 'text-gray-700'}>
                      {txn.amount_usd >= 0 ? '+' : ''}{txn.amount_usd.toFixed(4)}
                    </TableCell>
                    <TableCell className={txn.amount_usd >= 0 ? 'text-green-600 font-medium' : 'text-gray-700'}>
                      {txn.amount_usd >= 0 ? '+' : ''}{formatCurrency(Math.abs(txn.amount_usd), currency)}
                    </TableCell>
                    <TableCell className="text-gray-500 text-xs">{formatDate(txn.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </div>

      {/* Top Up Dialog */}
      <Dialog open={topUpOpen} onOpenChange={setTopUpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Top Up Balance</DialogTitle>
            <DialogDescription>
              Add funds to your Neoserve account. Processed securely via Xendit.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Select amount (USD)</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {TOP_UP_AMOUNTS_USD.map(amt => (
                  <button
                    key={amt}
                    onClick={() => { setTopUpAmount(amt); setCustomAmount('') }}
                    className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      topUpAmount === amt && !customAmount
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    ${amt}
                    <br />
                    <span className="text-xs text-gray-500 font-normal">
                      {formatCurrency(amt, currency)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Or enter custom amount (USD)</Label>
              <Input
                type="number"
                placeholder="Enter amount..."
                min="1"
                max="1000"
                value={customAmount}
                onChange={e => setCustomAmount(e.target.value)}
              />
            </div>

            {finalAmount > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                You'll be charged <strong>${finalAmount.toFixed(2)} USD</strong>
                {' '}(≈ {formatCurrency(finalAmount, currency)})
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTopUpOpen(false)}>Cancel</Button>
            <Button
              disabled={!finalAmount || finalAmount < 1}
              onClick={() => {
                toast({
                  title: 'Redirecting to payment...',
                  description: 'You will be redirected to Xendit to complete your payment.',
                  variant: 'default',
                })
                setTopUpOpen(false)
              }}
            >
              <CreditCard className="h-4 w-4" />
              Pay ${finalAmount.toFixed(2)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
